import React, { useState } from 'react';
import { Button, FieldGroup } from '@ver-uds/react';
import { Alert } from '@ver-uds/uswds-react';
import { format } from 'date-fns';
import { Account, AccountType } from 'eve-account-client';
import FullWidthLayout from '../../layouts/FullWidthLayout';
import HelpTextConstants from '../../constants/HelpTextConstants';
import ErrorBar from '../ErrorBar';
import SelectedCompanyLocationsAccordion from '../ManageCompanies/SelectedCompanyLocationsAccordion';

export type BulkTerminationRequestSummaryProps = {
  selectedAccounts: Account[];
  accountType: AccountType;
  title?: string;
  description?: string | JSX.Element;
};

const BulkTerminationRequestSummary = ({
  selectedAccounts,
  accountType,
  title,
  description,
}: BulkTerminationRequestSummaryProps): JSX.Element => {
  const [errorMessage] = useState<string | undefined>(undefined);

  const headerTitle = accountType === AccountType.AGENCY ? 'Client Companies' : 'Company Locations';

  let closedDate = '---';
  if (selectedAccounts[0]?.closedDate) {
    const dtDateOnly = new Date(selectedAccounts[0].closedDate);
    closedDate = format(dtDateOnly, 'E, MMM d, yyyy');
  }

  // This component is only used when terminating multiple company locations.
  // Thus we can assume the termination bulk termination request is today.
  // format date without timezone to prevent the date displayed from potentially being one day off
  const dtDateOnly = new Date();
  const terminationRequestDateShort = format(dtDateOnly, 'E, MMM d, yyyy');
  const terminationRequestDateLong = format(dtDateOnly, 'MMMM d, yyyy');

  const terminationCloseReason = selectedAccounts[0]?.closedRequestReason ?? '---';

  const summaryTitle = title ?? 'Termination Request Summary';
  const summaryDescription =
    description ??
    HelpTextConstants.TERMINATION_COMPANY_LOCATIONS_SUMMARY.replace(
      '{{ terminationRequestDate }}',
      terminationRequestDateLong,
    );

  const printTerminationRequest = (): void => {
    window.print();
  };

  const TerminationSummaryFieldGroups = () => {
    return (
      <FieldGroup className="grid-row">
        <FieldGroup.Field className="tablet:grid-col-6" name="Termination Request Date">
          {terminationRequestDateShort}
        </FieldGroup.Field>
        <FieldGroup.Field className="tablet:grid-col-6" name="Termination Date">
          <span data-testid="closed-date">{closedDate}</span>
        </FieldGroup.Field>
        <FieldGroup.Field className="tablet:grid-col-6" name="Termination Request Reason">
          {terminationCloseReason}
        </FieldGroup.Field>
      </FieldGroup>
    );
  };

  return (
    <FullWidthLayout title={summaryTitle} headerTitle={headerTitle}>
      <section className="padding-y-4 margin-bottom-3">
        <ErrorBar errorMessage={errorMessage} />
        <h1>{summaryTitle}</h1>
        <p className="margin-top-3 tablet:grid-col-9">{summaryDescription}</p>
        <Alert className="margin-top-3" status="warning">
          <Alert.Heading>You must:</Alert.Heading>
          <div>
            <ol className="usa-list">
              <li>Continue to follow the Memorandum of Understanding</li>
              <li>Resolve all open cases</li>
              <li>Not use E-Verify to create new cases</li>
            </ol>
          </div>
        </Alert>
        <div className="margin-top-3">
          <SelectedCompanyLocationsAccordion selectedAccounts={selectedAccounts} />
        </div>
        <div className="maxw-none">
          <TerminationSummaryFieldGroups />
          <hr />
          <div className="margin-top-4">
            <Button
              type="button"
              variant="secondary"
              aria-label="Print termination request summary"
              onClick={printTerminationRequest}
            >
              Print Information
            </Button>
          </div>
        </div>
      </section>
    </FullWidthLayout>
  );
};

export default BulkTerminationRequestSummary;
